import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  FormHelperText,
  Select,
  MenuItem,
  TablePagination,
  TableSortLabel,
  Toolbar,
  Divider,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import HomeExpenseGroupSelect from '../../components/inputs/HomeExpenseGroupSelect';
import { format, parseISO } from 'date-fns';
import EnhancedMonthlyPivot from './components/EnhancedMonthlyPivot';
import { 
  useHomeExpenses,
  useHomeExpensesSummary,
  useHomeExpenseGroups,
  useHomeExpensesCsvImport,
  useAccounts
} from '../../hooks/useHomeExpenses';
import { CreateHomeExpenseCommand, HomeExpenseFields, HomeExpenseSummaryDto, ExpenseType } from '../../types/homeExpenses';
import MultiExpenseForm from './MultiExpenseForm';
import PageHeader from '../../components/layout/PageHeader';
import TabPanel from '../../components/layout/TabPanel';
import AutoGrid from '../../components/layout/AutoGrid';

interface ExpensesByTypePageProps {
  expenseType: ExpenseType;
  title: string;
}

const ExpensesByTypePage: React.FC<ExpensesByTypePageProps> = ({ expenseType, title }) => {
  const [tabValue, setTabValue] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [multiAddDialogOpen, setMultiAddDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvAccountId, setCsvAccountId] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use the custom hooks
  const { 
    expenses, 
    loading: expensesLoading, 
    error: expensesError, 
    create: createExpenseAPI,
    createMultiple: createMultipleExpensesAPI,
    update: updateExpenseAPI,
    delete: deleteExpenseAPI,
    refresh: refreshExpenses
  } = useHomeExpenses(expenseType);

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<HomeExpenseFields | null>(null);
  const [savingInline, setSavingInline] = useState(false);
  
  const { 
    summary, 
    loading: summaryLoading 
  } = useHomeExpensesSummary(expenseType);
  
  // Note: useHomeExpenseGroups hook is available for future group management features
  const { groups } = useHomeExpenseGroups(expenseType);
  
  const { accounts } = useAccounts(expenseType);
  
  const { 
    loading: importLoading, 
    importFromCsv 
  } = useHomeExpensesCsvImport();

  // Form state
  const [newExpense, setNewExpense] = useState<CreateHomeExpenseCommand>({
    date: format(new Date(), 'yyyy-MM-dd'),
    homeExpenseGroupPublicId: '',
    amount: 0,
    accountPublicId: '',
    observations: '',
    type: expenseType,
  });

  // When accounts load, preselect the primary account if present and none chosen yet
  useEffect(() => {
    if (!newExpense.accountPublicId && accounts.length) {
      const primary = accounts.find(a => a.isPrimary);
      if (primary) {
        setNewExpense(prev => ({ ...prev, accountPublicId: primary.publicId }));
      }
    }
  }, [accounts, newExpense.accountPublicId]);

  // The CSV import goes into one account: start on the primary one
  useEffect(() => {
    if (!csvAccountId && accounts.length) {
      const primary = accounts.find(a => a.isPrimary) ?? accounts[0];
      setCsvAccountId(primary.publicId);
    }
  }, [accounts, csvAccountId]);

  // Filtering and pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<keyof any>('date');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedType, setSelectedType] = useState(''); // 'income', 'expense', or ''
  const [showFilters, setShowFilters] = useState(false);

  // Filtered and sorted expenses
  const filteredAndSortedExpenses = useMemo(() => {
    let filtered = [...expenses];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(expense =>
        expense.observations?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.group?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.account?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply date range filter
    if (dateFrom) {
      filtered = filtered.filter(expense => new Date(expense.date) >= new Date(dateFrom));
    }
    if (dateTo) {
      filtered = filtered.filter(expense => new Date(expense.date) <= new Date(dateTo));
    }

    // Apply account filter
    if (selectedAccount) {
      filtered = filtered.filter(expense => expense.account === selectedAccount);
    }

    // Apply group filter
    if (selectedGroup) {
      filtered = filtered.filter(expense => expense.group === selectedGroup);
    }

    // Apply type filter
    if (selectedType === 'income') {
      filtered = filtered.filter(expense => expense.isIncome);
    } else if (selectedType === 'expense') {
      filtered = filtered.filter(expense => !expense.isIncome);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[orderBy as keyof typeof a];
      const bValue = b[orderBy as keyof typeof b];
      
      // Handle undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return order === 'asc' ? -1 : 1;
      if (bValue == null) return order === 'asc' ? 1 : -1;
      
      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [expenses, searchTerm, dateFrom, dateTo, selectedAccount, selectedGroup, selectedType, orderBy, order]);

  // Paginated expenses
  const paginatedExpenses = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedExpenses.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedExpenses, page, rowsPerPage]);

  // Handler functions
  const handleRequestSort = (property: keyof any) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setSelectedAccount('');
    setSelectedGroup('');
    setSelectedType('');
    setPage(0);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddExpense = async () => {
    try {
      await createExpenseAPI(newExpense);
      setAddDialogOpen(false);
      setNewExpense({
        date: format(new Date(), 'yyyy-MM-dd'),
        homeExpenseGroupPublicId: '',
        amount: 0,
        accountPublicId: '',
        observations: '',
        type: expenseType,
      });
    } catch (error) {
      console.error('Failed to create expense:', error);
    }
  };

  const handleDeleteExpense = async (id: string) => {
    if (window.confirm('Tem a certeza de que pretende eliminar esta despesa?')) {
      try {
        await deleteExpenseAPI(id);
      } catch (error) {
        console.error('Failed to delete expense:', error);
      }
    }
  };

  const beginEdit = (expense: any) => {
    if (savingInline) return;
    setEditingId(expense.publicId);
    const inferredGroupId = expense.homeExpenseGroupPublicId || groups.find(g => g.name === expense.group)?.publicId || '';
    const inferredAccountId = expense.accountPublicId || accounts.find(a => a.name === expense.account)?.publicId || '';
    setEditDraft({
      date: expense.date.split('T')[0] || expense.date,
      homeExpenseGroupPublicId: inferredGroupId,
      amount: expense.amount,
      accountPublicId: inferredAccountId,
      observations: expense.observations || ''
    });
  };

  const cancelEdit = () => {
    if (savingInline) return;
    setEditingId(null);
    setEditDraft(null);
  };

  const saveEdit = async () => {
    if (!editingId || !editDraft) return;
    // basic validation
    if (!editDraft.homeExpenseGroupPublicId || !editDraft.accountPublicId || !editDraft.date) {
      alert('Preencha os campos obrigatórios (data, grupo, conta).');
      return;
    }
    if (editDraft.amount <= 0) {
      alert('O valor tem de ser superior a zero.');
      return;
    }
    try {
      setSavingInline(true);
      await updateExpenseAPI(editingId, editDraft);
      setEditingId(null);
      setEditDraft(null);
    } catch (err) {
      console.error('Failed to update expense', err);
      alert('Falha ao atualizar a despesa.');
    } finally {
      setSavingInline(false);
    }
  };

  const handleAddMultipleExpenses = async (expenses: HomeExpenseFields[]) => {
    try {
      // The rows carry no type; the API refuses a row without one
      await createMultipleExpensesAPI({ expenses: expenses.map(expense => ({ ...expense, type: expenseType })) });
      setMultiAddDialogOpen(false);
    } catch (error) {
      console.error('Failed to create multiple expenses:', error);
      throw error; // Re-throw to let the form handle the error display
    }
  };

  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Let the same file be chosen again after a failed import
    event.target.value = '';
    if (file && csvAccountId) {
      try {
        await importFromCsv(file, csvAccountId);
        setCsvDialogOpen(false);
        refreshExpenses(); // Refresh the list after import
        alert('CSV importado com sucesso!');
      } catch (error) {
        console.error('Failed to import CSV:', error);
        alert('Falha ao importar o CSV. Verifique o formato do ficheiro.');
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return format(parseISO(dateString), 'dd/MM/yyyy');
  };

  // Calculate totals with proper typing
  const totalIncome = summary.filter((s: HomeExpenseSummaryDto) => s.isIncomeGroup).reduce((sum: number, s: HomeExpenseSummaryDto) => sum + s.totalAmount, 0);
  const totalExpenses = summary.filter((s: HomeExpenseSummaryDto) => !s.isIncomeGroup).reduce((sum: number, s: HomeExpenseSummaryDto) => sum + s.totalAmount, 0);
  const netAmount = totalIncome - totalExpenses;

  if (expensesError) {
    return (
      <Box>
        <Alert severity="error">
          Falha ao carregar as despesas. Tente novamente mais tarde.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={title}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setCsvDialogOpen(true)}
            >
              Import CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setMultiAddDialogOpen(true)}
            >
              Adicionar várias
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Adicionar despesa
            </Button>
          </>
        }
      />

      {/* Summary Cards */}
      <AutoGrid min={150} sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IncomeIcon color="success" sx={{ mr: 1 }} />
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Total de receitas
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(totalIncome)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ExpenseIcon color="error" sx={{ mr: 1 }} />
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Total de despesas
                </Typography>
                <Typography variant="h6" color="error.main">
                  {formatCurrency(totalExpenses)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Saldo
            </Typography>
            <Typography 
              variant="h6" 
              color={netAmount >= 0 ? 'success.main' : 'error.main'}
            >
              {formatCurrency(netAmount)}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total de transações
            </Typography>
            <Typography variant="h6">
              {expenses.length}
            </Typography>
          </CardContent>
        </Card>
      </AutoGrid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" allowScrollButtonsMobile>
          <Tab label="Todas as despesas" />
          <Tab label="Por categoria" />
          <Tab label="Resumo" />
          <Tab label="Tabela mensal" />
        </Tabs>
      </Box>

      {/* All Expenses Tab */}
      <TabPanel value={tabValue} index={0}>
        {expensesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Filter Controls */}
            <Paper sx={{ mb: 2 }}>
              <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Pesquisar despesas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    sx={{ minWidth: 200 }}
                  />
                  <Button
                    variant={showFilters ? 'contained' : 'outlined'}
                    startIcon={<FilterIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    size="small"
                  >
                    Filtros
                  </Button>
                  {(searchTerm || dateFrom || dateTo || selectedAccount || selectedGroup || selectedType) && (
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                      size="small"
                    >
                      Limpar
                    </Button>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {filteredAndSortedExpenses.length} despesas
                </Typography>
              </Toolbar>

              {/* Expandable Filter Section */}
              {showFilters && (
                <>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Data de"
                          type="date"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Data até"
                          type="date"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Conta</InputLabel>
                          <Select
                            value={selectedAccount}
                            onChange={(e) => setSelectedAccount(e.target.value)}
                            label="Conta"
                          >
                            <MenuItem value="">Todas as contas</MenuItem>
                            {accounts.map((account) => (
                              <MenuItem key={account.publicId} value={account.name}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: account.color || '#9e9e9e',
                                    }}
                                  />
                                  {account.name}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <HomeExpenseGroupSelect
                          groups={groups}
                          value={(() => {
                            const match = groups.find(g => g.name === selectedGroup);
                            return match ? match.publicId : '';
                          })()}
                          onChange={(val) => {
                            const grp = groups.find(g => g.publicId === val);
                            setSelectedGroup(grp ? grp.name : '');
                          }}
                          size="small"
                          includeEmpty
                          label="Grupo"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Tipo</InputLabel>
                          <Select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                            label="Tipo"
                          >
                            <MenuItem value="">Todos os tipos</MenuItem>
                            <MenuItem value="income">Receita</MenuItem>
                            <MenuItem value="expense">Despesa</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}
            </Paper>

            <TableContainer component={Paper} sx={{ '& .MuiTableCell-root': { py: 0.5, '&:first-of-type': { pl: 1 }, '&:last-child': { pr: 1 } } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'date'}
                      direction={orderBy === 'date' ? order : 'asc'}
                      onClick={() => handleRequestSort('date')}
                    >
                      Data
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'group'}
                      direction={orderBy === 'group' ? order : 'asc'}
                      onClick={() => handleRequestSort('group')}
                    >
                      Grupo
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'amount'}
                      direction={orderBy === 'amount' ? order : 'asc'}
                      onClick={() => handleRequestSort('amount')}
                    >
                      Valor
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'account'}
                      direction={orderBy === 'account' ? order : 'asc'}
                      onClick={() => handleRequestSort('account')}
                    >
                      Conta
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Observações</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedExpenses.map((expense) => {
                  const isEditing = editingId === expense.publicId;
                  return (
                  <TableRow key={expense.publicId} hover selected={isEditing}>
                    <TableCell sx={{ fontSize: 12 }}>
                      {isEditing ? (
                        <TextField
                          type="date"
                          size="small"
                          value={editDraft?.date || ''}
                          onChange={(e)=> setEditDraft(d => d ? { ...d, date: e.target.value } : d)}
                          sx={{ minWidth: 130 }}
                        />
                      ) : formatDate(expense.date)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {isEditing ? (
                        <HomeExpenseGroupSelect
                          groups={groups}
                          value={editDraft?.homeExpenseGroupPublicId || ''}
                          onChange={(val) => setEditDraft(d => d ? { ...d, homeExpenseGroupPublicId: val } : d)}
                          size="small"
                          includeEmpty
                        />
                      ) : expense.group}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 500, color: expense.isIncome ? 'success.main' : 'error.main' }}>
                      {isEditing ? (
                        <TextField
                          type="number"
                          size="small"
                          value={editDraft?.amount ?? ''}
                          onChange={(e)=> setEditDraft(d => d ? { ...d, amount: parseFloat(e.target.value) || 0 } : d)}
                          sx={{ maxWidth: 110 }}
                          inputProps={{ step: 0.01, min: 0 }}
                        />
                      ) : formatCurrency(expense.amount)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {isEditing ? (
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <Select
                            value={editDraft?.accountPublicId || ''}
                            onChange={(e)=> setEditDraft(d => d ? { ...d, accountPublicId: e.target.value } : d)}
                          >
                            <MenuItem value="">Selecionar...</MenuItem>
                            {accounts.sort((a,b)=> a.name.localeCompare(b.name)).map(acc => (
                              <MenuItem key={acc.publicId} value={acc.publicId}>{acc.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : expense.account}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, maxWidth: 220 }}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          value={editDraft?.observations || ''}
                          onChange={(e)=> setEditDraft(d => d ? { ...d, observations: e.target.value } : d)}
                          placeholder="Observações"
                          sx={{ minWidth: 160 }}
                        />
                      ) : (
                        <Box sx={{
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }} title={expense.observations}>{expense.observations}</Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      <Chip
                        label={expense.isIncome ? 'Receita' : 'Despesa'}
                        color={expense.isIncome ? 'success' : 'error'}
                        size="small"
                        sx={{ height: 20 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0, display:'flex', gap:0.5 }}>
                      {isEditing ? (
                        <>
                          <IconButton size="small" onClick={saveEdit} disabled={savingInline} aria-label="guardar">
                            <SaveIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEdit} disabled={savingInline} aria-label="cancelar">
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton size="small" onClick={()=> beginEdit(expense)} aria-label="editar">
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteExpense(expense.publicId)}
                            aria-label="eliminar despesa"
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );})}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Linhas por página"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`}
              component="div"
              count={filteredAndSortedExpenses.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
          </>
        )}
      </TabPanel>

      {/* By Category Tab */}
      <TabPanel value={tabValue} index={1}>
        {summaryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {summary.map((categoryData) => (
              <Accordion key={categoryData.group}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, width: '100%', pr: 2 }}>
                    <Typography variant="h6">{categoryData.group}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <Chip
                        label={categoryData.isIncomeGroup ? 'Receita' : 'Despesa'}
                        color={categoryData.isIncomeGroup ? 'success' : 'error'}
                        size="small"
                      />
                      <Typography 
                        variant="h6" 
                        color={categoryData.isIncomeGroup ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(categoryData.totalAmount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({categoryData.count} transações)
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Data</TableCell>
                          <TableCell>Valor</TableCell>
                          <TableCell>Conta</TableCell>
                          <TableCell>Observações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {expenses
                          .filter(expense => expense.group === categoryData.group)
                          .map((expense) => (
                            <TableRow key={expense.publicId}>
                              <TableCell>{formatDate(expense.date)}</TableCell>
                              <TableCell>
                                <Typography 
                                  color={expense.isIncome ? 'success.main' : 'error.main'}
                                >
                                  {formatCurrency(expense.amount)}
                                </Typography>
                              </TableCell>
                              <TableCell>{expense.account}</TableCell>
                              <TableCell>{expense.observations}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Summary Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="success.main">
                  Categorias de receitas
                </Typography>
                {summary
                  .filter(s => s.isIncomeGroup)
                  .map((category) => (
                    <Box key={category.group} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>{category.group}</Typography>
                      <Typography color="success.main">{formatCurrency(category.totalAmount)}</Typography>
                    </Box>
                  ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="error.main">
                  Categorias de despesas
                </Typography>
                {summary
                  .filter(s => !s.isIncomeGroup)
                  .map((category) => (
                    <Box key={category.group} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>{category.group}</Typography>
                      <Typography color="error.main">{formatCurrency(category.totalAmount)}</Typography>
                    </Box>
                  ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Monthly Pivot Tab */}
      <TabPanel value={tabValue} index={3}>
        <EnhancedMonthlyPivot
          expenses={expenses}
          groups={groups}
          formatCurrency={formatCurrency}
        />
      </TabPanel>

      {/* Add Expense Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar despesa</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                type="date"
                label="Data"
                value={newExpense.date}
                onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <HomeExpenseGroupSelect
                groups={groups}
                value={newExpense.homeExpenseGroupPublicId}
                onChange={(val) => setNewExpense({ ...newExpense, homeExpenseGroupPublicId: val })}
                size="medium"
                includeEmpty={false}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                type="number"
                label="Valor"
                value={newExpense.amount}
                onChange={(e) => setNewExpense({ ...newExpense, amount: parseFloat(e.target.value) || 0 })}
              />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Conta</InputLabel>
                <Select
                  value={newExpense.accountPublicId}
                  onChange={(e) => setNewExpense({ ...newExpense, accountPublicId: e.target.value })}
                  label="Conta"
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.publicId} value={account.publicId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: account.color || '#9e9e9e',
                          }}
                        />
                        {account.name}
                        {account.isPrimary && (
                          <Chip label="Principal" size="small" color="primary" />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={2}
                value={newExpense.observations}
                onChange={(e) => setNewExpense({ ...newExpense, observations: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleAddExpense} variant="contained">Adicionar</Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)}>
        <DialogTitle>Importar CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Carregue um ficheiro CSV com as seguintes colunas: Data, Grupo, Montante, Conta, Observações
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Conta</InputLabel>
            <Select
              value={csvAccountId}
              onChange={(e) => setCsvAccountId(e.target.value)}
              label="Conta"
            >
              {accounts.map((account) => (
                <MenuItem key={account.publicId} value={account.publicId}>{account.name}</MenuItem>
              ))}
            </Select>
            <FormHelperText>Todas as despesas do ficheiro ficam nesta conta.</FormHelperText>
          </FormControl>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleCsvUpload}
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            fullWidth
            disabled={importLoading || !csvAccountId}
            startIcon={importLoading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {importLoading ? 'A importar...' : 'Escolher ficheiro CSV'}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialogOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Multi-Expense Form Dialog */}
      <MultiExpenseForm
        open={multiAddDialogOpen}
        onClose={() => setMultiAddDialogOpen(false)}
        onSubmit={handleAddMultipleExpenses}
        groups={groups}
        accounts={accounts}
        loading={expensesLoading}
      />
    </Box>
  );
};

export default ExpensesByTypePage;

